import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { VariableSizeGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import NoEntriesIcon from '@material-ui/icons/SentimentVerySatisfiedOutlined';
import { withStyles } from '@material-ui/core/styles';
import CardOrder from './cardOrder';
import { caseInsensitiveMatch } from '../../utils/case-insensitive-match';
import Button from '@material-ui/core/Button';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import AccordionOrder from './accordionOrder';


const cleanLeadingZeros = (text) => {
  if (!text) {
    return text;
  }

  return text.replace(/\b0+/g, '');
};

class OrderGrid extends Component {
  constructor(props) {
    super(props);
    this.mainRef = React.createRef();

    const { isInApp } = props;

    this.state = {
      isInApp,
      didActivateSound: isInApp,
    };
    this.orderRefs = [];
    this.listRef = React.createRef();
  }

  componentDidMount() {
    this.props.registerScrollTopOrderGridFunction(this.scrollToTop.bind(this));
    this.checkForListRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props && (nextProps.orders && nextProps.orders != this.props.orders || (nextProps.orderSearchValue != this.props.orderSearchValue))) {
      setTimeout(() => {
        this.checkForListRef();
      }, 1000);
    }
  }

  get itemsPerRow() {
    const width = window.innerWidth;
    if (width < 600) {
      return 1;
    }
    if (width < 960) {
      return 2;
    }
    if (width < 1450) {
      return 3;
    }
    return 4;
  }

  checkForListRef = () => {
    if (!this.listRef || !this.listRef.current) {
      setTimeout(this.checkForListRef, 100);
      return;
    }
    this.listRef.current.resetAfterIndices({ columnIndex: 0, rowIndex: 0 });
  }

  checkForListReset = () => {
    if (this.listRef && this.listRef.current) {
      let firstEntryIndex = 0;
      for (let i = 0; i < this.orderRefs.length; i++) {
        if (this.orderRefs[i]) {
          firstEntryIndex = i;
          i = this.orderRefs.length;
        }
      }
      const rowIndex = firstEntryIndex > 0 ? Math.floor(firstEntryIndex / this.itemsPerRow) : 0;
      this.listRef.current.resetAfterRowIndex(rowIndex);
    }
  }

  scrollToTop() {
    // only scroll when fullscreen-mode is active
    if (((window.fullScreen) || (window.innerWidth === window.screen.width && window.innerHeight === window.screen.height))) { this.mainRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  }

  renderCardOrder = ({
    columnIndex, rowIndex, style, data,
  }) => {
    const {
      proceedOrder,
      undoOrder,
      declineOrder,
      showTotalPrice,
      openDeliveryDialog,
      printOrder,
      exportOrder,
      printingorders,
      isRoutePlanning,
      addOrderToRoute,
      removeOrderFromRoute,
      routestops,
      localSettings,
      selectOrderForDeliveryTimeInfo,
      exportingOrders,
    } = this.props;


    const index = (rowIndex * this.itemsPerRow) + columnIndex;
    const order = data[index];

    if (!order) {
      return null;
    }

    return (
      <div
        ref={(ref) => { this.orderRefs[index] = ref; }}
        style={{ ...style, padding: '10px 5px' }}
        key={order.orderID}
      >
        <CardOrder
          value={order}
          showTotalPrice={showTotalPrice}
          proceedOrder={proceedOrder}
          undoOrder={undoOrder}
          declineOrder={declineOrder}
          printOrder={printOrder}
          exportOrder={exportOrder}
          openDeliveryDialog={openDeliveryDialog}
          printing={printingorders[order.id]}
          exporting={exportingOrders[order.id]}
          isRoutePlanning={isRoutePlanning}
          addOrderToRoute={addOrderToRoute}
          removeOrderFromRoute={removeOrderFromRoute}
          isIncludedInRoute={routestops.includes(order.id)}
          localSettings={localSettings}
          selectOrderForDeliveryTimeInfo={selectOrderForDeliveryTimeInfo}
        />
      </div>
    );
  }

  getRowHeight = (index) => {
    const start = index * this.itemsPerRow;
    const end = start + this.itemsPerRow;
    const rowOrders = this.orderRefs.slice(start, end);
    if (!rowOrders || !rowOrders.find(r => !!r)) {
      return 200;
    }

    let rowHeight = 0;
    rowOrders.forEach((rowOrder) => {
      let height = 15;

      if (rowOrder && rowOrder.children) {
        for (let i = 0; i < rowOrder.children.length; i++) {
          height += rowOrder.children[i].offsetHeight;
        }
      }

      if (height > rowHeight) {
        rowHeight = height;
      }
    });
    return rowHeight;
  };

  renderAccordionOrder = (order, allOrders) => {
    const {
      proceedOrder,
      declineOrder,
      showTotalPrice,
      openDeliveryDialog,
      printOrder,
      printingorders,
      isRoutePlanning,
      addOrderToRoute,
      removeOrderFromRoute,
      routestops,
      localSettings,
      selectOrderForDeliveryTimeInfo,
    } = this.props;

    if (!order)
      return <></>;

    return (
      <AccordionOrder
        value={order}
        showTotalPrice={showTotalPrice}
        proceedOrder={proceedOrder}
        declineOrder={declineOrder}
        printOrder={printOrder}
        expanded={allOrders.length == 1 ? true : undefined}
        openDeliveryDialog={openDeliveryDialog}
        printing={printingorders[order.id]}
        isRoutePlanning={isRoutePlanning}
        addOrderToRoute={addOrderToRoute}
        removeOrderFromRoute={removeOrderFromRoute}
        isIncludedInRoute={routestops.includes(order.id)}
        localSettings={localSettings}
        selectOrderForDeliveryTimeInfo={selectOrderForDeliveryTimeInfo}
      />
    );
  }

  renderOrders(orderList) {
    const { localSettings } = this.props;

    const disableMobileList = localSettings.disableMobileList;
    if (!orderList || !orderList.length) {
      return null;
    }

    return (
      <>
        <Hidden xsDown={!disableMobileList}>
          <AutoSizer>
            {({ width, height }) => (
              <VariableSizeGrid
                columnCount={this.itemsPerRow}
                columnWidth={() => (width / this.itemsPerRow) - (1 * this.itemsPerRow)}
                height={height}
                rowCount={Math.ceil(orderList.length / this.itemsPerRow)}
                rowHeight={index => this.getRowHeight(index)}
                width={width}
                itemData={orderList}
                ref={this.listRef}
                itemCount={orderList.length}
                onScroll={this.checkForListReset}
              >
                {this.renderCardOrder}
              </VariableSizeGrid>
            )}
          </AutoSizer>
        </Hidden>
        
        {
          disableMobileList ? null : (
            <Hidden smUp>
              {orderList.map((order) => this.renderAccordionOrder(order, orderList))}
            </Hidden>
          )
        }
      </>
    );
  }

  render() {
    const {
      classes,
      orderSearchValue,
      loading,
      routestops,
      localSettings,
      openRouteDialog,
    } = this.props;

    const filterByTerm = (order) => {
      const searchTermMatch = caseInsensitiveMatch.bind(null, cleanLeadingZeros(orderSearchValue));
      const matchCode = searchTermMatch(order.pickupCode);
      const matchName = searchTermMatch(order.pickupName);
      const matchOwnGuid = searchTermMatch(order.guid);
      const matchGuid = order.parentOrderGuid && searchTermMatch(order.parentOrderGuid.replace(/[^0-9]+/g, ''))
      const matchUser = orderSearchValue && orderSearchValue.length > 4 && order.userPublicIdentifier && searchTermMatch(order.userPublicIdentifier.replace(/[^0-9]+/g, ''));

      return matchCode || matchName || matchUser || matchGuid || matchOwnGuid;
    };

    const orderCount = this.props.orders ? this.props.orders.length : 0;

    const orderList = orderSearchValue ? this.props.orders.filter(filterByTerm) : this.props.orders;

    const loadingIndicator = loading
      ? <LinearProgress className={classes.loadingIndicator} />
      : null;

    const noEntriesFound = !loading && orderList.length === 0
      ? (<div className={classes.noEntriesFound}>
        <NoEntriesIcon className={classes.noEntriesFoundIcon} />
        <Typography className={classes.noEntriesFoundText}>Alle Bestellungen abgearbeitet</Typography>
      </div>)
      : null;


    const activateSound = this.state.didActivateSound ? null : (
      <div className={classes.soundButton}>
        <Button
          variant="transparent"
          onClick={() => {
            requestAnimationFrame(() => {
              this.props.playSound();
            });
            this.setState({ didActivateSound: true });
          }}
        >
          <span>Tonsignal aktivieren</span><VolumeUpIcon />
        </Button>
      </div>
    );

    const rootStyle = noEntriesFound
      ? {
        alignItems: 'center',
        flexDirection: 'row',
      }
      : {};

    return (
      <div ref={this.mainRef} style={{ width: '100%' }}>
        <Grid container item direction="column" style={{ ...rootStyle, height: '100%', paddingLeft: 5, paddingRight: 5 }}>
          {loadingIndicator}
          {activateSound ? null : noEntriesFound}
          {this.renderOrders(orderList)}
          {noEntriesFound && activateSound}
        </Grid>
        <Hidden xsDown>
          {routestops.length > 0 && localSettings.enableRouteplanning ?
            <Grid
              container
              className={classes.bottomButtonsContainer}
              justify="flex-end"
            >

              <Grid
                item
                xs={12}
                lg={9}
                xl={10}
                className={classes.showroutebuttonContainer}
              >
                <Button
                  variant="outlined"
                  className={classes.showroutebutton}
                  onClick={() => openRouteDialog()}
                >Route berechnen</Button>
              </Grid>

              {/* Placeholder instead of sidebar */}
              {localSettings.showSidebar ?
                <Hidden mdDown>
                  <Grid item lg={3} xl={2} />
                </Hidden>
                : null}
            </Grid>
            : null}
        </Hidden>
      </div>
    );
  }
}

const styles = theme => ({
  root: {
  },
  grid: {
    padding: 10,
  },
  noEntriesFound: {
    padding: 10,
    flexGrow: 1,
    textAlign: 'center',
  },
  noEntriesFoundText: {
    color: theme.palette.text.hint,
    fontSize: 16,
  },
  noEntriesFoundIcon: {
    color: theme.palette.text.secondary,
    fontSize: 150,
  },
  soundButton: {
    flexGrow: 1,
    textAlign: 'center',
  },
  loadingIndicator: {
    width: '100%',
  },
  showroutebutton: {
    fontSize: theme.order.buttons.fontSize,
    margin: '2px',
    backgroundColor: theme.backgroundcolor,
  },
  bottomButtonsContainer: {
    position: 'absolute',
    top: 'calc(100vh - 54px)',
    width: '100%',
  },
  showroutebuttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

OrderGrid.propTypes = {
  classes: PropTypes.object.isRequired,
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isRoutePlanning: PropTypes.bool,
  proceedOrder: PropTypes.func.isRequired,
  undoOrder: PropTypes.func.isRequired,
  declineOrder: PropTypes.func.isRequired,
  openDeliveryDialog: PropTypes.func.isRequired,
  orderSearchValue: PropTypes.string,
  showTotalPrice: PropTypes.bool.isRequired,
  localSettings: PropTypes.object.isRequired,
  printingorders: PropTypes.object,
  routestops: PropTypes.array.isRequired,
  openRouteDialog: PropTypes.func.isRequired,
  printOrder: PropTypes.func.isRequired,
  exportOrder: PropTypes.func.isRequired,
  registerScrollTopOrderGridFunction: PropTypes.func.isRequired,
  addOrderToRoute: PropTypes.func.isRequired,
  removeOrderFromRoute: PropTypes.func.isRequired,
  selectOrderForDeliveryTimeInfo: PropTypes.func.isRequired,
};

OrderGrid.defaultProps = {
  orderSearchValue: null,
  printingorders: {},
  isRoutePlanning: false,
};

export default withStyles(styles)(OrderGrid);
